import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { useSeo } from "@/hooks/useSeo";

const values = [
  {
    icon: "ri-compass-3-line",
    title: "内容为先",
    desc: "持续扩充影视资源库，坚持分类清晰、资料准确，让每一次浏览都高效有价值。",
  },
  {
    icon: "ri-shield-check-line",
    title: "体验为本",
    desc: "从筛选到详情，从桌面到移动端，打磨每一处交互细节，专注流畅稳定的使用体验。",
  },
  {
    icon: "ri-lightbulb-line",
    title: "推荐有度",
    desc: "基于类型与主创相似度做温和推荐，帮助你发现更多可能喜欢的作品，而不打扰选择。",
  },
];

const milestones = [
  { year: "2021", text: "天堂电影网立项，确立“影视内容导航平台”的核心定位。" },
  { year: "2022", text: "上线电影与电视剧双频道，初步建立多维筛选与分类体系。" },
  { year: "2023", text: "推出排行榜与专题推荐，用户浏览深度显著提升。" },
  { year: "2024", text: "全面升级移动端体验，新增本地收藏与智能推荐功能。" },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 天堂电影网",
    description:
      "了解天堂电影网的平台定位、发展愿景与法律声明。我们致力于为广大影迷提供清晰、丰富、更新及时的影视内容导航体验。",
    keywords: "天堂电影网,关于我们,影视平台介绍,发展愿景,版权声明",
  });

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <SiteHeader />
      <main className="flex-1 pt-16 md:pt-20">
        <section className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20cinematic%20wide%20banner%20of%20deep%20blue%20light%20rays%20through%20dark%20theater%20space%20with%20floating%20film%20frames%2C%20elegant%20minimal%20moody%20atmosphere%2C%20high%20detail&width=1600&height=800&seq=about-hero-banner&orientation=landscape"
            alt="天堂电影网品牌背景"
            title="关于天堂电影网"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 to-transparent" />
          <div className="relative h-full w-full px-4 md:px-8 flex items-center">
            <div className="max-w-[1400px] mx-auto w-full">
              <span className="inline-flex items-center gap-1 rounded-full bg-accent-500/10 border border-accent-500/40 px-3 py-1 text-xs text-accent-300">
                <i className="ri-information-line" aria-hidden="true" />
                About Us
              </span>
              <h1 className="mt-4 font-heading text-3xl md:text-5xl font-bold text-foreground-950">关于天堂电影网</h1>
              <p className="mt-3 text-sm md:text-base text-foreground-400 max-w-2xl">
                我们是一群热爱电影的人，希望用清晰的结构与贴心的推荐，让好作品更容易被看见。
              </p>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            <div>
              <span className="w-1.5 h-7 rounded-full bg-primary-500 inline-block mb-4" aria-hidden="true" />
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">网站定位</h2>
              <p className="mt-4 text-sm md:text-base text-foreground-400 leading-loose">
                天堂电影网是一个面向广大影视爱好者的内容导航平台，尤其服务于喜欢在线观看高清电影与电视剧的用户群体。网站核心目标是提供丰富、分类明确且更新及时的影视资源，帮助用户便捷地浏览、筛选与查看影片详情。
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-400 leading-loose">
                内容覆盖最新发行的电影、电视剧以及部分经典影片，辅以排行榜与相关推荐，让用户在浏览中不断发现新惊喜。我们相信，好的影视导航应该像一位懂你的朋友，知道你想看什么。
              </p>
            </div>
            <div>
              <span className="w-1.5 h-7 rounded-full bg-accent-500 inline-block mb-4" aria-hidden="true" />
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">发展愿景</h2>
              <p className="mt-4 text-sm md:text-base text-foreground-400 leading-loose">
                我们希望成为影迷最信赖的影视内容入口：不仅告诉你“有什么可看”，更能帮你判断“值不值得看”。未来，天堂电影网将继续深化内容体系，丰富专题策划与个性化推荐，并不断优化跨设备的浏览体验。
              </p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {values.map((value) => (
                  <div key={value.title} className="rounded-lg bg-background-100 ring-1 ring-background-200/70 p-4">
                    <span className="w-9 h-9 flex items-center justify-center rounded-md bg-secondary-800 text-accent-300 text-lg">
                      <i className={value.icon} aria-hidden="true" />
                    </span>
                    <h3 className="mt-3 text-sm font-semibold text-foreground-900">{value.title}</h3>
                    <p className="mt-1.5 text-xs text-foreground-500 leading-relaxed">{value.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100">
          <div className="max-w-[1400px] mx-auto">
            <span className="w-1.5 h-7 rounded-full bg-primary-500 inline-block mb-4" aria-hidden="true" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">发展历程</h2>
            <div className="mt-8 space-y-0">
              {milestones.map((item, index) => (
                <div key={item.year} className="flex gap-5 md:gap-8">
                  <div className="flex flex-col items-center">
                    <span className="w-3 h-3 rounded-full bg-accent-500 shrink-0 mt-1.5" aria-hidden="true" />
                    {index !== milestones.length - 1 && <span className="w-px flex-1 bg-background-300/60 my-1" />}
                  </div>
                  <div className="pb-8">
                    <span className="font-heading text-lg font-bold text-accent-400">{item.year}</span>
                    <p className="mt-1 text-sm text-foreground-400 leading-relaxed max-w-2xl">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="copyright" className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="max-w-[1400px] mx-auto rounded-lg bg-secondary-900/50 ring-1 ring-secondary-700/60 p-6 md:p-10">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-10 flex items-center justify-center rounded-md bg-secondary-800 text-accent-300 text-xl">
                <i className="ri-copyright-line" aria-hidden="true" />
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">版权与法律声明</h2>
            </div>
            <div className="space-y-4 text-sm text-foreground-400 leading-loose">
              <p>
                本站为影视内容导航与信息展示平台，所提供的影片名称、简介、演职人员等资料均整理自公开信息，仅供影迷交流与学习参考，不提供任何视听内容的播放、下载或存储服务。
              </p>
              <p>
                本站所涉及的影视剧照、海报等素材版权归原版权方所有。如相关权利人对内容存在异议，请通过“联系我们”页面与我们取得联系，我们将在核实后及时作出处理。
              </p>
              <p>
                用户在浏览本站过程中产生的收藏数据仅保存在用户本地浏览器，本站不会收集、上传或用于其他用途。本站保留对本声明的最终解释权。
              </p>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}